// Courses.tsx (Updated with Base Fee Display)
import React, { useState, useEffect, useRef } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Menu } from 'primereact/menu';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useSuperAdminDashboardStore } from '../../stateManagers/stores/superAdminDashboardStore';
import AddCourseModal from './AddCourseModal';
import CustomConfirmDialog from '../../components/CustomConfirmDialog';
import AddSubjectModal from '../Subjects/AddSubjectModal';
import AddFeesSlabModal from './AddFeesSlabModal';

interface Subject {
  subject_id: string;
  subject_name: string;
}

interface FeesSlab {
  fees_slab_id: string;
  base_fee: number;
  minimum_fee: number;
  maximum_fee: number;
  status: string;
  created_at: string | null;
}

interface Course {
  course_id: string;
  institute_id: string;
  name: string;
  title: string;
  description: string | null;
  status: string;
  subjects: Subject[];
  fees_slab: FeesSlab | null;
  created_at: string | null;
}

interface ApiResponse {
  success: boolean;
  data: Course[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

// ── HCI brand tokens (from the logo) ──────────────────────────────────────────
const C = {
  navy: '#14267A', navyDeep: '#0B1650', gold: '#F5B326',
  tint: '#E8EBF8', tintSoft: '#F4F5FA', page: '#F4F5FA',
  line: '#E2E5F2', lineStrong: '#DADFF0',
  text: '#12183A', textSoft: '#5A6080',
};

const Courses: React.FC = () => {
  const toast = useRef<Toast>(null);
  const menuRefs = useRef<{ [key: string]: Menu | null }>({});
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddSubjectModal, setShowAddSubjectModal] = useState(false);
  const [showAddFeesSlabModal, setShowAddFeesSlabModal] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [selectedCourseName, setSelectedCourseName] = useState<string>('');
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null); // card turns navy on hover
  const [deleteDialog, setDeleteDialog] = useState<{
    visible: boolean;
    courseId: string;
    courseName: string;
  }>({
    visible: false,
    courseId: '',
    courseName: ''
  });

  const { data: dashboardData, loading: dashboardLoading, fetchDashboardData } = useSuperAdminDashboardStore();

  useEffect(() => {
    if (!dashboardData) {
      fetchDashboardData();
    }
  }, [dashboardData, fetchDashboardData]);


  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .p-menu {
        border-radius: 12px !important;
        border: 1px solid #E2E5F2 !important;
        box-shadow: 0 8px 24px rgba(20,38,122,0.14) !important;
        padding: 6px !important;
      }
      .p-menu .p-menuitem-link { border-radius: 8px !important; padding: 8px 11px !important; margin: 2px 0 !important; transition: all 0.2s ease !important; }
      .p-menu .p-menuitem-link:hover { background: #E8EBF8 !important; }
      .p-menu .p-menuitem-icon { color: #14267A !important; font-size: 0.8rem !important; }
      .p-menu .p-menuitem-text { color: #12183A !important; font-weight: 600 !important; font-size: 0.75rem !important; }
      .subject-chip { position: relative; }
      .subject-chip:hover::after {
        content: attr(data-tooltip);
        position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%);
        background: #14267A; color: white; padding: 5px 10px; border-radius: 6px;
        font-size: 0.7rem; white-space: nowrap; z-index: 100; pointer-events: none;
        box-shadow: 0 3px 10px rgba(20,38,122,0.25); margin-bottom: 8px;
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [page, search]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...(search && { search })
      });

      const response = await apiClient.get<ApiResponse>(`/v1/courses?${params}`);
      
      if (response.data.success) {
        setCourses(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to load courses');
      }
    } catch (err: any) {
      console.error('Failed to fetch courses:', err);
      setError(err.response?.data?.error || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCourse = async (courseId: string, courseName: string) => {
    setDeleteDialog({
      visible: true,
      courseId,
      courseName
    });
  };

  const confirmDelete = async () => {
    try {
      await apiClient.delete(`/v1/courses/${deleteDialog.courseId}`);
      
      toast.current?.show({
        severity: 'success',
        summary: 'Deleted',
        detail: 'Course deleted successfully',
        life: 3000
      });
      
      fetchCourses();
    } catch (err: any) {
      console.error('Failed to delete course:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.error || 'Failed to delete course',
        life: 4000
      });
    }
  };

  const handleEditCourse = (course: Course) => {
    setEditingCourse(course);
    setShowAddModal(true);
  };

  const handleAddSubject = (courseId: string, courseName: string) => {
    setSelectedCourseId(courseId);
    setSelectedCourseName(courseName);
    setShowAddSubjectModal(true);
  };

  const handleAddFeesSlab = (courseId: string, courseName: string) => {
    setSelectedCourseId(courseId);
    setSelectedCourseName(courseName);
    setShowAddFeesSlabModal(true);
  };

  const getMenuItems = (course: Course) => [
    {
      label: 'Add Subject',
      icon: 'pi pi-plus-circle',
      command: () => handleAddSubject(course.course_id, course.name)
    },
    {
      label: 'Add Fees Slab',
      icon: 'pi pi-money-bill',
      command: () => handleAddFeesSlab(course.course_id, course.name)
    },
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: () => handleEditCourse(course)
    },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      command: () => handleDeleteCourse(course.course_id, course.name)
    }
  ];

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (dashboardLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <ProgressSpinner 
          style={{ width: '50px', height: '50px' }} 
          strokeWidth="3"
        />
        <p style={{ color: '#14267A', fontSize: '0.9rem' }}>Loading dashboard data...</p>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div style={{ padding: '24px' }}>
        <Message
          severity="warn"
          text="Unable to load dashboard data. Please refresh the page."
          style={{ fontSize: '0.9rem' }}
        />
      </div>
    );
  }

  if (loading && courses.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        background: '#F4F5FA',
        borderRadius: '18px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner 
            style={{ width: '50px', height: '50px' }} 
            strokeWidth="3"
          />
          <p style={{
            marginTop: '16px',
            color: '#14267A',
            fontSize: '0.95rem'
          }}>
            Loading courses...
          </p>
        </div>
      </div>
    );
  }


  // ── Summary numbers (based on the courses loaded on this page) ──────────────
  const activeCount = courses.filter((c) => c.status === 'active').length;
  const feesCount = courses.filter((c) => c.fees_slab).length;
  const subjectsTotal = courses.reduce((sum, c) => sum + (c.subjects?.length || 0), 0);

  const stats = [
    { icon: 'pi-book',          label: 'Total courses',     value: pagination.total, tag: 'All',       iconBg: C.tint,    iconFg: C.navy },
    { icon: 'pi-check-circle',  label: 'Active courses',    value: activeCount,      tag: 'This page', iconBg: '#E3F6C4', iconFg: '#2C5A12' },
    { icon: 'pi-bookmark',      label: 'Subjects',          value: subjectsTotal,    tag: 'This page', iconBg: C.tint,    iconFg: C.navy },
    { icon: 'pi-wallet',        label: 'With fees slab',    value: feesCount,        tag: 'This page', iconBg: '#FFE9BF', iconFg: '#8A4B00' },
  ];

  const sectionLabel = (): React.CSSProperties => ({
    fontSize: '0.585rem', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase',
    color: C.textSoft, marginBottom: '6px',
  });

  return (
    <div style={{ padding: '0', margin: '0 auto', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", color: C.text }}>
      <Toast ref={toast} />
      <CustomConfirmDialog
        visible={deleteDialog.visible}
        onHide={() => setDeleteDialog({ visible: false, courseId: '', courseName: '' })}
        onConfirm={confirmDelete}
        title="Delete Course"
        message={`Are you sure you want to delete "${deleteDialog.courseName}"? This action cannot be undone and all associated data will be permanently removed.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        severity="danger"
        icon="pi pi-trash"
      />

      {/* ── Toolbar ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '20px' }}>
        <div style={{ position: 'relative', width: '360px', maxWidth: '100%' }}>
          <i className="pi pi-search" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: C.textSoft, fontSize: '0.85rem' }}></i>
          <InputText
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search courses by name or title..."
            style={{ width: '100%', paddingLeft: '40px', height: '40px', border: `1px solid ${C.lineStrong}`, borderRadius: '12px', fontSize: '0.8rem', background: 'white' }}
          />
        </div>
        <button
          onClick={() => { setEditingCourse(null); setShowAddModal(true); }}
          style={{
            display: 'flex', alignItems: 'center', gap: '10px', height: '40px', padding: '0 20px',
            borderRadius: '12px', border: 'none', background: C.navy, color: 'white',
            fontFamily: 'inherit', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
            boxShadow: '0 10px 22px rgba(20,38,122,0.28)', transition: 'background 0.2s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = C.navyDeep; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = C.navy; }}
        >
          <i className="pi pi-plus" style={{ fontSize: '0.85rem' }}></i>
          Add Course
        </button>
      </div>

      {/* ── Summary ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {stats.map((st) => (
          <div key={st.label} style={{ background: 'white', border: `1px solid ${C.line}`, borderRadius: '16px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: st.iconBg, color: st.iconFg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className={`pi ${st.icon}`} style={{ fontSize: '0.95rem' }}></i>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.65rem', fontWeight: 600, color: C.textSoft }}>{st.label}</div>
              <div style={{ fontSize: '1.15rem', fontWeight: 800, color: C.text, lineHeight: 1.2 }}>{st.value}</div>
            </div>
            <span style={{ fontSize: '0.585rem', fontWeight: 700, padding: '3px 8px', borderRadius: '999px', background: '#EEF0F8', color: '#4A5070' }}>{st.tag}</span>
          </div>
        ))}
      </div>

      {error && (
        <Message severity="error" text={error} style={{ marginBottom: '16px', fontSize: '0.8rem', fontWeight: 500 }} />
      )}

      {/* ── Courses grid ── */}
      {courses.length === 0 && !loading ? (
        <div style={{ padding: '64px 20px', textAlign: 'center', background: 'white', borderRadius: '18px', border: `1px dashed ${C.lineStrong}` }}>
          <i className="pi pi-book" style={{ fontSize: '2.2rem', color: '#C6CDE8', marginBottom: '12px' }}></i>
          <h3 style={{ margin: '0 0 6px 0', color: C.text, fontSize: '0.95rem', fontWeight: 700 }}>No courses found</h3>
          <p style={{ margin: 0, color: C.textSoft, fontSize: '0.78rem' }}>
            {search ? 'Try adjusting your search criteria' : 'Get started by adding your first course'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: '14px' }}>
          {courses.map((course) => {
            const hov = hoveredId === course.course_id;
            const isActive = course.status === 'active';
            const soft = C.textSoft;
            const subjects = course.subjects || [];

            return (
              <div
                key={course.course_id}
                onMouseEnter={() => setHoveredId(course.course_id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  background: hov ? '#EEF3FF' : 'white',
                  color: C.text,
                  border: `1px solid ${hov ? '#C6D4FF' : C.line}`,
                  borderRadius: '18px',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  transform: hov ? 'scale(1.02)' : 'scale(1)',
                  transition: 'transform 0.25s ease, background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
                  boxShadow: hov ? '0 10px 24px rgba(20,38,122,0.10)' : '0 1px 3px rgba(20,38,122,0.04)',
                }}
              >
                {/* Top row: icon + status */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '11px', background: C.tint, color: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="pi pi-book" style={{ fontSize: '0.95rem' }}></i>
                  </div>
                  <span style={{
                    padding: '3px 10px', borderRadius: '999px', fontSize: '0.625rem', fontWeight: 700, textTransform: 'capitalize',
                    background: isActive ? '#E3F6C4' : '#FDE2E2', color: isActive ? '#2C5A12' : '#B42318',
                  }}>
                    {course.status}
                  </span>
                </div>

                {/* Course name + course title (both, labelled) */}
                <div style={{ marginBottom: '10px' }}>
                  <div style={sectionLabel()}>Course name</div>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '0.9rem', fontWeight: 700, color: C.text, letterSpacing: '-0.2px' }}>{course.name || '—'}</h3>
                  <div style={sectionLabel()}>Course title</div>
                  <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: 600, color: C.text, lineHeight: 1.4 }}>{course.title || '—'}</p>
                </div>

                {/* Description */}
                {course.description && (
                  <p style={{
                    margin: '0 0 12px 0', fontSize: '0.72rem', color: soft, lineHeight: 1.5,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                  }}>
                    {course.description}
                  </p>
                )}

                {/* Fee + subjects count tiles */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                  <div style={{ background: hov ? 'white' : C.tintSoft, borderRadius: '11px', padding: '8px 10px' }}>
                    <div style={sectionLabel()}>Starting fee</div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: C.navy }}>
                      {course.fees_slab ? formatCurrency(course.fees_slab.base_fee) : '—'}
                    </div>
                  </div>
                  <div style={{ background: hov ? 'white' : C.tintSoft, borderRadius: '11px', padding: '8px 10px' }}>
                    <div style={sectionLabel()}>Subjects</div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: C.text }}>{subjects.length}</div>
                  </div>
                </div>

                {/* Subject avatars */}
                <div style={{ minHeight: '30px', display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                  {subjects.length > 0 ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {subjects.slice(0, 4).map((subject, index) => (
                          <div
                            key={subject.subject_id}
                            className="subject-chip"
                            data-tooltip={subject.subject_name}
                            style={{
                              width: '28px', height: '28px', borderRadius: '50%',
                              background: C.navy, color: C.gold,
                              border: `2px solid ${hov ? '#EEF3FF' : 'white'}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.65rem', fontWeight: 800, cursor: 'default',
                              marginLeft: index > 0 ? '-9px' : '0', zIndex: 4 - index,
                            }}
                          >
                            {subject.subject_name.charAt(0).toUpperCase()}
                          </div>
                        ))}
                      </div>
                      {subjects.length > 4 && (
                        <span style={{ marginLeft: '8px', fontSize: '0.68rem', color: soft, fontWeight: 600 }}>+{subjects.length - 4} more</span>
                      )}
                    </>
                  ) : (
                    <span style={{ fontSize: '0.7rem', color: soft, fontStyle: 'italic' }}>No subjects added</span>
                  )}
                </div>

                {/* Footer: created + actions */}
                <div style={{ marginTop: 'auto' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.65rem', color: soft, marginBottom: '10px' }}>
                    <i className="pi pi-calendar" style={{ fontSize: '0.65rem' }}></i>
                    Created {formatDate(course.created_at)}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      style={{
                        flex: 1, height: '36px', border: 'none', borderRadius: '11px',
                        background: C.navy, color: 'white',
                        fontFamily: 'inherit', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <i className="pi pi-eye" style={{ fontSize: '0.8rem' }}></i>
                      View Details
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); menuRefs.current[course.course_id]?.toggle(e); }}
                      aria-label="More actions"
                      style={{
                        width: '36px', height: '36px', border: 'none', borderRadius: '11px',
                        background: C.tint, color: C.navy,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease',
                      }}
                    >
                      <i className="pi pi-ellipsis-v" style={{ fontSize: '0.8rem' }}></i>
                    </button>
                    <Menu
                      model={getMenuItems(course)}
                      popup
                      ref={(el) => { menuRefs.current[course.course_id] = el; }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Pagination ── */}
      {pagination.pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '20px', padding: '10px', background: 'white', border: `1px solid ${C.line}`, borderRadius: '16px' }}>
          {([
            { icon: 'pi-chevron-left',  disabled: page === 1,                  onClick: () => setPage((p) => Math.max(1, p - 1)) },
            { icon: 'pi-chevron-right', disabled: page === pagination.pages,   onClick: () => setPage((p) => Math.min(pagination.pages, p + 1)) },
          ]).map((b, i) => (
            <React.Fragment key={b.icon}>
              {i === 1 && (
                <span style={{ fontSize: '0.78rem', color: C.navy, fontWeight: 700, padding: '0 8px' }}>
                  Page {pagination.page} of {pagination.pages}
                </span>
              )}
              <button
                disabled={b.disabled}
                onClick={b.onClick}
                style={{
                  width: '34px', height: '34px', border: 'none', borderRadius: '10px',
                  background: b.disabled ? C.tint : C.navy, color: b.disabled ? '#9AA0BE' : 'white',
                  cursor: b.disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <i className={`pi ${b.icon}`} style={{ fontSize: '0.8rem' }}></i>
              </button>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Add/Edit Course Modal */}
      <AddCourseModal
        visible={showAddModal}
        editingCourse={editingCourse}
        instituteId={dashboardData?.institutes[0]?.institute_id || ''}
        onHide={() => {
          setShowAddModal(false);
          setEditingCourse(null);
        }}
        onSuccess={() => {
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: editingCourse ? 'Course updated successfully!' : 'Course created successfully!',
            life: 3000
          });
          fetchCourses();
          setShowAddModal(false);
          setEditingCourse(null);
        }}
      />

      {/* Add Subject Modal */}
      <AddSubjectModal
        visible={showAddSubjectModal}
        courseId={selectedCourseId}
        courseName={selectedCourseName}
        onHide={() => {
          setShowAddSubjectModal(false);
          setSelectedCourseId('');
          setSelectedCourseName('');
        }}
        onSuccess={() => {
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Subject created successfully!',
            life: 3000
          });
          fetchCourses();
          setShowAddSubjectModal(false);
          setSelectedCourseId('');
          setSelectedCourseName('');
        }}
      />

      {/* Add Fees Slab Modal */}
      <AddFeesSlabModal
        visible={showAddFeesSlabModal}
        courseId={selectedCourseId}
        courseName={selectedCourseName}
        instituteId={dashboardData?.institutes[0]?.institute_id || ''}
        onHide={() => {
          setShowAddFeesSlabModal(false);
          setSelectedCourseId('');
          setSelectedCourseName('');
        }}
        onSuccess={() => {
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Fees slab created successfully!',
            life: 3000
          });
          fetchCourses();
          setShowAddFeesSlabModal(false);
          setSelectedCourseId('');
          setSelectedCourseName('');
        }}
      />
    </div>
  );
};

export default Courses;